/**
 * `/me/welcome` — first-time-member welcome interstitial.
 *
 * Closes Tom (member) UX log F2 P0 + Dispatch 2 (verbatim from
 * `docs/audits/ux-log-tom-member-2026-05-01.md` §4):
 *
 *   > F2 — Post-sign-in / pre-team-page: No member-flavoured welcome
 *   > interstitial. Tom is dropped into the AppShell with a "Member"
 *   > chip but no "here's how your data is treated" preamble. Add a
 *   > 1-screen first-time-member welcome with the three-circles
 *   > privacy model. Single biggest structural gap for member adoption.
 *
 * Why a new route (vs. extending `OnboardingPage.tsx`): the canonical
 * onboarding wizard is a lead-side three-step team-creation flow
 * (`OnboardingPage.tsx:63-117`). Member-side onboarding has different
 * shape (no team creation, no invite step, no setup hand-off) — keeping
 * lead + member onboarding separable lets each speak to its audience
 * without role-conditional branching tangling both flows.
 *
 * Round H Brief 4 placed a `<ThreeCirclesPrivacyStrip tone="focal"/>`
 * interstitial on `/me/audit` first-entry as a stopgap. That surface
 * works once a worker reaches `/me/audit` — but per Tom's "60-second
 * exit risk", a wary worker landing on `/teams/<id>` first sees the
 * AppShell chrome with no privacy framing. This page sits BEFORE that:
 * a member's first authenticated render gets redirected here by the
 * `<MemberWelcomeGate/>` wrapper in `App.tsx`.
 *
 * Dismissal model
 * ───────────────
 * One localStorage flag, shared with the MeAudit interstitial:
 *
 *   `tarii.privacy.three_circles.dismissed.<accountId>` = "1"
 *
 * Per the brief's fallback rule: dismissing here implicitly dismisses
 * the MeAudit strip too (one trust-pillar moment per session, not two).
 * The `usePrivacyInterstitialDismissed` hook in `MeAudit.tsx:191-232`
 * already reads this exact key, so no MeAudit code change is needed —
 * the worker who clicks "Get started" (or "Skip for now") here sees
 * the privacy callout card on `/me/audit` rather than the focal strip.
 *
 * Brand-pillar discipline
 * ───────────────────────
 * - Banlist-clean: NO "tracking" / "monitoring" / "surveillance" /
 *   "violation" / "breach" / "audit" tokens (the page title says
 *   "Welcome", not "audit log walkthrough"; the link to `/me/audit`
 *   says "Activity history").
 * - Calmer noun discipline: "your activity history" not "audit"; "your
 *   load" not "your performance"; "your forecast" not "your
 *   trajectory analysis".
 * - Plural "we" voice per `feedback_brand_organization_shape.md`.
 *
 * Focal-component discipline (`feedback_ui_section_focal_component.md`)
 * ─────────────────────────────────────────────────────────────────────
 * The page anchors on a SINGLE focal element — the
 * `<ThreeCirclesPrivacyStrip tone="focal"/>`. The PageLead is
 * supporting context (orientation); the link cards below recede to
 * font-normal supporting weight; the "Get started" CTA is the primary
 * action button. Avoid the uniform-grid trap: link cards are visually
 * subordinate to the strip.
 *
 * Self-contained chrome (matches OnboardingPage pattern)
 * ──────────────────────────────────────────────────────
 * No AppShell wrapper — the welcome surface is its own page like
 * `/onboarding`. This gives the focal three-circles strip clean
 * vertical space without competing chrome (workspace switcher pill,
 * "Member" chip, Schedule ▾ dropdown). The worker reads the trust
 * pillar before the chrome loads.
 *
 * Copy v1 — flagged for founder override
 * ──────────────────────────────────────
 * The PageLead title + body + CTA labels are v1 brand-pillar-checked
 * but not yet user-tested. Per `ThreeCirclesPrivacyStrip.tsx`'s own
 * convention, treat these strings as v1 and surface for founder review
 * during the next brand pass.
 */
import { ArrowRight, CalendarDays, Inbox, SquareCheckBig, Table2, Users } from "lucide-react";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { Logo } from "../../components/Logo";
import { ThreeCirclesPrivacyStrip } from "../../components/ThreeCirclesPrivacyStrip";
import { Button, PageLead } from "../../components/ui";
import { useAuth } from "../../lib/AuthContext";
import { useActiveMeWorkspace } from "../../lib/useActiveMeTeamId";
import {
  type MemberAppTabKey,
  deriveMemberAppTabs,
  showsTeamTab,
} from "../../memberapp/memberTabs";

/**
 * Shared with `MeAudit.tsx:191` — one flag dismisses both surfaces so
 * a worker sees the trust pillar once per account-on-this-browser, not
 * twice (once on /me/welcome, once on /me/audit). Per the brief's
 * fallback rule.
 */
export const PRIVACY_INTERSTITIAL_DISMISSED_KEY_PREFIX = "tarii.privacy.three_circles.dismissed.";

/**
 * Pure helper — exported so the gate (`<MemberWelcomeGate/>` in
 * App.tsx) and the welcome page can both reason about whether the
 * worker has already seen a trust-pillar surface.
 *
 * Privacy-mode browsers throw on `localStorage.getItem`; we treat
 * that as "not dismissed" so the worker still gets the trust copy,
 * matching the MeAudit hook's posture.
 */
export function readPrivacyDismissed(accountId: string | null): boolean {
  if (!accountId) return false;
  if (typeof window === "undefined") return false;
  try {
    return (
      window.localStorage.getItem(`${PRIVACY_INTERSTITIAL_DISMISSED_KEY_PREFIX}${accountId}`) ===
      "1"
    );
  } catch {
    return false;
  }
}

function writePrivacyDismissed(accountId: string | null): void {
  if (!accountId) return;
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${PRIVACY_INTERSTITIAL_DISMISSED_KEY_PREFIX}${accountId}`, "1");
  } catch {
    // Best-effort persistence; the in-memory navigation still completes.
  }
}

/**
 * #1665 (§5.6) — the welcome is the member APP's first-run now. The
 * second moment introduces the app's tabs (three, plus #3063's Week sheet
 * and #2945's Team on a roster team); "Get started" lands on the app's index, Team today or the
 * Calendar (fixing the old drift where it landed on the activity page). Rendered as one scrollable screen rather than two
 * paged ones so the primary CTA is present from first paint — the
 * live-prod capture harness (`tests/e2e/capture_demo_shots.py`
 * `dismiss_welcome`) and the existing tests click "Get started"
 * directly.
 */
interface AppTabIntro {
  description: string;
  icon: typeof Inbox;
}

/**
 * #3078 — one line per tab of the bottom bar, keyed by the bar's own tab
 * keys. The rows and the heading's count come from `deriveMemberAppTabs`,
 * so the welcome names the tabs the bar shows: five on a roster team (with
 * #3063's Week sheet), three on a Taskmap team.
 */
const APP_TAB_INTROS: Record<MemberAppTabKey, AppTabIntro> = {
  inbox: { description: "What needs you — and nothing that doesn't.", icon: Inbox },
  calendar: {
    description: "Your time: shifts, on-call, leave — one timetable.",
    icon: CalendarDays,
  },
  sheet: {
    description: "The whole team's week: who works on each ward, and who is on call.",
    icon: Table2,
  },
  tasks: {
    description: "Your requests — leave, swaps, preferences.",
    icon: SquareCheckBig,
  },
  // #2945 — Team on a roster team, for every role.
  team: {
    description: "Who works with you today: your ward, the other side, on call.",
    icon: Users,
  },
};

const TAB_COUNT_WORDS: Record<number, string> = { 3: "Three", 4: "Four", 5: "Five" };

export function MeWelcome() {
  const { accountId } = useAuth();
  const navigate = useNavigate();
  // #2945, #3063 — a roster team adds the Week sheet and Team, for every role
  // (`showsTeamTab`); the list is the tab bar's own.
  const tabs = deriveMemberAppTabs({ teamTab: showsTeamTab(useActiveMeWorkspace()) });
  const countWord = TAB_COUNT_WORDS[tabs.length] ?? String(tabs.length);

  const dismissAndContinue = useCallback(() => {
    writePrivacyDismissed(accountId);
    // #1665 §5.6 — first-run lands on the app's index (was `/me/audit`, a
    // drift the design calls out and fixes). #2945 — the index opens a member
    // of a roster team on Team today when this is the visit's first screen,
    // and the Calendar otherwise (`memberapp/openingScreen.ts`).
    navigate("/me", { replace: true });
  }, [accountId, navigate]);

  return (
    <main className="mx-auto flex min-h-screen max-w-3xl flex-col gap-8 px-4 py-12 sm:px-6">
      <header className="flex items-center justify-between">
        <Logo />
        {/* Flows finding 41 — the sm Button is 32 px; beside the 44 px
            "Get started" the skip meets the same `tap` minimum. */}
        <Button
          variant="ghost"
          size="sm"
          onClick={dismissAndContinue}
          data-testid="me-welcome-skip"
          className="min-h-tap"
        >
          Skip for now
        </Button>
      </header>

      <PageLead
        eyebrow="Welcome"
        title="Welcome to Tarii"
        subtitle="What stays yours, what your lead sees, what your team sees."
      />

      {/* Focal element — the three-circles privacy strip. The single
          load-bearing trust-pillar surface for member adoption per
          `feedback_privacy_assurance_for_team_usage.md`. Tone is
          `focal` so it carries the section's visual weight. */}
      <section
        aria-label="How your data sits on Tarii"
        data-testid="me-welcome-focal"
        className="flex flex-col gap-3"
      >
        <ThreeCirclesPrivacyStrip audience="member" tone="focal" showTitle={false} />
      </section>

      {/* Supporting context (§5.6 second moment) — the app's three
          concepts, quiet rows under the focal strip. Not links: the app
          itself is one tap away via "Get started"; teaching happens by
          arrival, not by a link farm. */}
      <section
        aria-label="The app's tabs"
        data-testid="me-welcome-surfaces"
        className="flex flex-col gap-3"
      >
        <h2 className="text-sm font-semibold text-fg">{countWord} tabs, everything you need</h2>
        <ul className="flex flex-col gap-2">
          {tabs.map((tab) => {
            const { icon: Icon, description } = APP_TAB_INTROS[tab.key];
            return (
              <li
                key={tab.key}
                data-testid={`me-welcome-tab-${tab.key}`}
                className="flex items-start gap-3 border-t border-border pt-2.5 first:border-t-0 first:pt-0"
              >
                <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-fg-muted" aria-hidden="true" />
                <span className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-fg">{tab.label}</span>
                  <span className="text-xs text-fg-muted">{description}</span>
                </span>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Primary CTA row. "Get started" is the focal action; "Skip for
          now" already lives in the header. Both write the same
          localStorage flag (per brief fallback rule) so dismissing
          either surface dismisses the MeAudit strip too. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
        {/* Flows finding 26 — the md Button is 36 px; the member's first tap
            meets the app's 44 px `tap` minimum. */}
        <Button
          type="button"
          onClick={dismissAndContinue}
          data-testid="me-welcome-get-started"
          className="min-h-tap"
        >
          Get started
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </main>
  );
}

export default MeWelcome;
